import request from '@/utils/request'
import { useAppStore } from '@/stores/app'
import validationUtil from '@/utils/validationUtil'

interface NotificationGroup {
  groupId: number
  groupName: string
  groupDescription: string
  canUpdateNotificationGroup: boolean
  shopIds: number[]
  groupType?: string
  members?: any[]
}

interface Strategy {
  name: string
  id: number
}

interface Member {
  groupId: number
  [key: string]: any
}

interface PreparedNotificationGroup {
  groupId: number
  groupName: string
  groupDescription: string
  canUpdateNotificationGroup: boolean
  shopIds: Array<{ shopId: number }>
  members: Member[] | number
}

type NotificationGroupUpdateResponse =
  | number
  | string
  | {
      id?: number | string
      groupId?: number | string
      data?: number | string | { id?: number | string; groupId?: number | string }
      request?: { response?: number | string }
    }

const notificationService = {
  async getNotificationGroups(): Promise<void> {
    const path = '/notification/GetNotificationGroups'
    const store = useAppStore()
    
    const result = await request.get<NotificationGroup[]>(
      import.meta.env.VITE_REVENUE_API_URL + path
    )

    if (result) {
      const filteredResult = result
        .map((shop) => {
          if (shop.shopIds[0] === 0) {
            shop.shopIds.pop()
          }
          return shop
        })
        .filter((shop) => shop.groupType !== 'ShoppingCenter')

      store.setNotificationGroups(filteredResult)
    }
  },

  async postGroup(body: Partial<NotificationGroup>): Promise<Response> {
    const path = '/notification/UpdateNotificationGroup'
    return await request.post(import.meta.env.VITE_REVENUE_API_URL + path, body)
  },

  validateGroupFields(fields: any[]): string[] {
    const fieldsValidationResult = validationUtil.checkFieldValidity(fields)
    const errors: string[] = []
    
    if (!fieldsValidationResult) {
      errors.push('FormInvalid')
    }
    return errors
  },

  getStrategies(): Strategy[] {
    const store = useAppStore()
    
    const result: Strategy[] = [
      { name: 'Level1', id: 1 },
      { name: 'Level2', id: 2 },
      { name: 'Level3', id: 3 },
      { name: 'NoNotifications', id: 9 },
    ]
    
    store.setStrategies(result)
    return result
  },

  canCreateGroup(notificationsGroupList: NotificationGroup[]): boolean {
    return notificationsGroupList?.some((group) => group.canUpdateNotificationGroup) ?? false
  },

  prepareNotificationDetails(
    notificationGroupList: NotificationGroup[],
    memberList: Member[]
  ): PreparedNotificationGroup[] {
    if (notificationGroupList) {
      const distinctGroupList: PreparedNotificationGroup[] = []
      
      notificationGroupList.forEach((element) => {
        const relatedMembers = memberList?.filter((x) => x.groupId === element.groupId)
        
        const groupExists = distinctGroupList.find((g) => g.groupId === element.groupId)
        
        const relatedShops = element.shopIds
          ? element.shopIds.map((shopId) => ({ shopId: shopId }))
          : []

        if (groupExists === undefined) {
          distinctGroupList.push({
            groupId: element.groupId,
            groupName: element.groupName,
            groupDescription: element.groupDescription,
            canUpdateNotificationGroup: element.canUpdateNotificationGroup,
            shopIds: relatedShops,
            members: relatedMembers ?? [],
          })
        }
      })
      return distinctGroupList
    } else {
      return []
    }
  },

  addNewGroup(groupToAdd: NotificationGroup): void {
    const store = useAppStore()
    store.createGroup(groupToAdd)
  },

  async updateNotificationGroupFull(
    group: Partial<NotificationGroup>, 
    isNew: boolean, 
    shopId?: number
  ): Promise<void> {
    const store = useAppStore()
    
    const payload = {
      roleId: group.groupId,
      roleName: group.groupName,
      roleDescription: group.groupDescription ?? ''
    }

    const result = await this.postGroup(payload as any)

    if (isNew) {
      const response = result as NotificationGroupUpdateResponse
      let rawGroupId: number | string | undefined

      if (typeof response === 'number' || typeof response === 'string') {
        rawGroupId = response
      } else if (response && typeof response === 'object') {
        if (typeof response.data === 'number' || typeof response.data === 'string') {
          rawGroupId = response.data
        } else if (response.data && typeof response.data === 'object') {
          rawGroupId = response.data.groupId ?? response.data.id
        }

        rawGroupId ??= response.groupId
        rawGroupId ??= response.id
        rawGroupId ??= response.request?.response
      }

      const parsedGroupId =
        typeof rawGroupId === 'string'
          ? Number.parseInt(rawGroupId, 10)
          : rawGroupId

      if (typeof parsedGroupId !== 'number' || Number.isNaN(parsedGroupId)) {
        throw new Error('Missing group id from UpdateNotificationGroup response')
      }
      
      const newGroup: NotificationGroup = {
        groupId: parsedGroupId,
        groupName: group.groupName!,
        groupDescription: group.groupDescription!,
        canUpdateNotificationGroup: true,
        shopIds: [shopId ?? 0],
        groupType: 'Shop'
      }
      
      store.createGroup(newGroup)
    } else {
      // Ensure we update the store with the values we just sent
      store.updateGroup({
        groupId: Number(group.groupId), // Ensure number for comparison
        groupName: group.groupName,
        groupDescription: group.groupDescription
      })
    }
  }
}

export default notificationService
